import React, { useEffect, useRef, lazy, Suspense } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { I18nProvider } from "./lib/i18n";
import { AuthProvider, useAuth } from "./lib/auth";
import api from "./lib/api";
import { initPushNotifications, setPushNavigator } from "./lib/native";
import Landing from "./pages/Landing";
import FormFill from "./pages/FormFill";
import TicketPage from "./pages/TicketPage";

const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const AccountPage = lazy(() => import("./pages/AccountPage"));
const LegalPage = lazy(() => import("./pages/LegalPage"));
const MyTickets = lazy(() => import("./pages/MyTickets"));
const LiveDisplay = lazy(() => import("./pages/LiveDisplay"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const FormEditor = lazy(() => import("./pages/FormEditor"));
const SubmissionsView = lazy(() => import("./pages/SubmissionsView"));
const ScannerPage = lazy(() => import("./pages/ScannerPage"));
const SupervisorsPage = lazy(() => import("./pages/SupervisorsPage"));
const AttendancePrint = lazy(() => import("./pages/AttendancePrint"));
const StatsPage = lazy(() => import("./pages/StatsPage"));
const AdminSupportPage = lazy(() => import("./pages/AdminSupportPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));
const CertificatePage = lazy(() => import("./pages/CertificatePage"));
const VerifyPage = lazy(() => import("./pages/VerifyPage"));
const SurveyPage = lazy(() => import("./pages/SurveyPage"));
const InvitePage = lazy(() => import("./pages/InvitePage"));
const BroadcastPage = lazy(() => import("./pages/BroadcastPage"));
const LeaderboardAdminPage = lazy(() => import("./pages/LeaderboardAdminPage"));
const MyHoursPage = lazy(() => import("./pages/MyHoursPage"));
const HoursCertificatePage = lazy(() => import("./pages/HoursCertificatePage"));
const VerifyHoursPage = lazy(() => import("./pages/VerifyHoursPage"));
const HoursAdminPage = lazy(() => import("./pages/HoursAdminPage"));

const Loader = (
  <div className="min-h-screen grid place-items-center bg-[#FDFBF7]">
    <div className="animate-pulse text-slate-400 text-sm">…</div>
  </div>
);

function ProtectedRoute({ children, adminOnly = false, staffOnly = false, superOnly = false }) {
  const { user } = useAuth();
  if (user === null) return Loader;
  if (!user) return <Navigate to="/login" replace />;
  if (superOnly && !user.is_super) return <Navigate to="/admin" replace />;
  if (adminOnly && user.role !== "admin")
    return <Navigate to={user.role === "supervisor" ? "/admin/scanner" : "/account"} replace />;
  if (staffOnly && !["admin", "supervisor"].includes(user.role))
    return <Navigate to="/account" replace />;
  return children;
}

function PushDeepLink() {
  const navigate = useNavigate();
  const navRef = useRef(navigate);
  navRef.current = navigate;
  useEffect(() => {
    setPushNavigator((url) => navRef.current(url));
  }, []);
  useEffect(() => {
    let handle;
    let cancelled = false;
    import("@capacitor/core").then(({ Capacitor }) => {
      if (!Capacitor.isNativePlatform()) return;
      import("@capacitor/app").then(({ App: CapApp }) => {
        CapApp.addListener("appUrlOpen", ({ url }) => {
          try {
            const u = new URL(url);
            navRef.current(u.pathname + u.search);
          } catch {}
        }).then((h) => {
          if (cancelled) h.remove();
          else handle = h;
        });
      });
    });
    return () => {
      cancelled = true;
      if (handle) handle.remove();
    };
  }, []);
  return null;
}

function App() {
  useEffect(() => {
    initPushNotifications((path, body) => api.post(path, body));
  }, []);
  return (
    <div className="App">
      <I18nProvider>
        <AuthProvider>
          <BrowserRouter>
            <PushDeepLink />
            <Toaster position="bottom-right" richColors closeButton />
            <Suspense fallback={Loader}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/f/:formId" element={<FormFill />} />
                <Route path="/ticket/:code" element={<TicketPage />} />
                <Route path="/certificate/:code" element={<CertificatePage />} />
                <Route path="/verify/:code" element={<VerifyPage />} />
                <Route path="/survey/:code" element={<SurveyPage />} />
                <Route path="/invite/:token" element={<InvitePage />} />
                <Route path="/live/:formId" element={<LiveDisplay />} />
                <Route path="/verify-hours/:userId/:year" element={<VerifyHoursPage />} />
                <Route
                  path="/my-hours"
                  element={
                    <ProtectedRoute>
                      <MyHoursPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/hours-certificate/:year"
                  element={
                    <ProtectedRoute>
                      <HoursCertificatePage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/my-tickets" element={<MyTickets />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ForgotPasswordPage />} />
                <Route path="/privacy" element={<LegalPage kind="privacy" />} />
                <Route path="/terms" element={<LegalPage kind="terms" />} />
                <Route
                  path="/account"
                  element={
                    <ProtectedRoute>
                      <AccountPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/support"
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminSupportPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/audit"
                  element={
                    <ProtectedRoute adminOnly>
                      <AuditLogPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/broadcast"
                  element={
                    <ProtectedRoute superOnly>
                      <BroadcastPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/leaderboard"
                  element={
                    <ProtectedRoute adminOnly>
                      <LeaderboardAdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/login" element={<LoginPage />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminDashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/forms/new"
                  element={
                    <ProtectedRoute adminOnly>
                      <FormEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/forms/:formId/edit"
                  element={
                    <ProtectedRoute adminOnly>
                      <FormEditor />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/forms/:formId/submissions"
                  element={
                    <ProtectedRoute adminOnly>
                      <SubmissionsView />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/forms/:formId/attendance-print"
                  element={
                    <ProtectedRoute>
                      <AttendancePrint />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/stats"
                  element={
                    <ProtectedRoute staffOnly>
                      <StatsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/hours"
                  element={
                    <ProtectedRoute adminOnly>
                      <HoursAdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/supervisors"
                  element={
                    <ProtectedRoute adminOnly>
                      <SupervisorsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/scanner"
                  element={
                    <ProtectedRoute>
                      <ScannerPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </I18nProvider>
    </div>
  );
}

export default App;
